import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { NotificationRepository } from '../application/NotificationRepository'
import {
  notificationPageSchema,
  notificationPreferencesSchema,
  notificationSummarySchema,
  type NotificationScope,
} from '../domain/notification'

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

/** Filtre vide = « tous » : jamais envoyé, le serveur refuse une clé inconnue. */
function scopeArgs(scope: NotificationScope): Record<string, string> {
  return {
    ...(scope.category ? { category: scope.category } : {}),
    ...(scope.priority ? { priority: scope.priority } : {}),
    ...(scope.period ? { period: scope.period } : {}),
  }
}

/** Aucun repli sur des fixtures : un faux compteur se lirait comme un fait. */
export function createSupabaseNotificationRepository(client: AppSupabaseClient): NotificationRepository {
  const rpcClient = client as unknown as SupabaseClient

  async function call(name: string, args?: Record<string, unknown>): Promise<unknown> {
    const response = (await rpcClient.rpc(name, args)) as unknown as RpcResponse
    if (response.error)
      throw new Error(`NOTIFICATIONS_${name.toUpperCase()}_FAILED:${response.error.message}`)
    return response.data
  }

  return {
    async getSummary() {
      return notificationSummarySchema.parse(await call('notifications_summary_v1'))
    },
    async list(filter) {
      return notificationPageSchema.parse(
        await call('notifications_list_v1', {
          p_filter: { tab: filter.tab, ...scopeArgs(filter), limit: filter.limit, offset: filter.offset },
        }),
      )
    },
    async setRead(ids, read) {
      return z
        .number()
        .int()
        .parse(await call('notification_set_read_v1', { p_ids: ids, p_read: read }))
    },
    async markAllRead(scope) {
      return z
        .number()
        .int()
        .parse(await call('notification_mark_all_read_v1', { p_filter: scopeArgs(scope) }))
    },
    async setArchived(ids, archived) {
      return z
        .number()
        .int()
        .parse(await call('notification_set_archived_v1', { p_ids: ids, p_archived: archived }))
    },
    async getPreferences() {
      return notificationPreferencesSchema.parse(await call('notification_preferences_v1'))
    },
    async savePreferences(preferences) {
      return notificationPreferencesSchema.parse(
        await call('notification_save_preferences_v1', { p_command: { preferences } }),
      )
    },
  }
}
